import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import FullCalendarComponent from './components/Calendar';
import MemoPage from './components/Memo';
import S3FilesPage from './components/S3Files';
import AppHeader from './components/Common/AppHeader';
import { Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import './App.css';
import './index.css';
import { useAuthenticator } from '@aws-amplify/ui-react';

// App は index.js から user/signOut を受け取る場合があるので、
// props を優先し、無ければ useAuthenticator から得るようにします。
function App(props) {
  const parentUser = props.user;
  const parentSignOut = props.signOut;
  // useAuthenticator は必ず同じ順序で呼ぶ（条件付き禁止）
  const authCtx = useAuthenticator((c) => [c.user, c.signOut]);
  const user = parentUser ?? authCtx.user ?? null;
  const signOut = parentSignOut ?? authCtx.signOut ?? (() => {});
  const [userGroups, setUserGroups] = React.useState([]);
  const [isDesk, setIsDesk] = React.useState(false);

  // 追加: auth セッション情報を取得して子コンポーネントに確実に渡す
  const [authSession, setAuthSession] = React.useState(null);
  React.useEffect(() => {
    if (!user) {
      setAuthSession(null);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        // まず専用パッケージを試し、なければ汎用を試す
        const mod = await import('@aws-amplify/auth').catch(() => null) || await import('aws-amplify').catch(() => null);
        if (!mounted || !mod) return;
        // fetchAuthSession があれば優先的に使ってトークンを抽出
        if (typeof mod.fetchAuthSession === 'function') {
          const session = await mod.fetchAuthSession().catch(() => null);
          if (!mounted || !session) return;
          let idToken = null, accessToken = null;
          try { idToken = session.getIdToken && session.getIdToken().getJwtToken && session.getIdToken().getJwtToken(); } catch (e) {}
          try { accessToken = session.getAccessToken && session.getAccessToken().getJwtToken && session.getAccessToken().getJwtToken(); } catch (e) {}
          if (mounted) setAuthSession({ session, idToken, accessToken });
          return;
        }
        // currentAuthenticatedUser が使える場合は user オブジェクトごと取得
        if (typeof mod.currentAuthenticatedUser === 'function') {
          const u = await mod.currentAuthenticatedUser().catch(() => null);
          if (!mounted) return;
          const idToken = u && u.signInUserSession && (u.signInUserSession.idToken && (u.signInUserSession.idToken.jwtToken || u.signInUserSession.idToken.raw)) ? (u.signInUserSession.idToken.jwtToken || u.signInUserSession.idToken.raw) : null;
          const accessToken = u && u.signInUserSession && u.signInUserSession.accessToken && u.signInUserSession.accessToken.jwtToken ? u.signInUserSession.accessToken.jwtToken : null;
          if (mounted) setAuthSession({ user: u, idToken, accessToken });
          return;
        }
      } catch (e) {
        console.error('failed to resolve auth session', e);
      }
    })();
    return () => { mounted = false; };
  }, [user]);

  // スマホ+タブレット判定（PCは1024px以上）
  const isMobile = useMediaQuery('(max-width:1023px)');

  React.useEffect(() => {
    // user オブジェクト（useAuthenticator 由来）からグループを取得する
    if (user) {
      try {
        const groups = (user.signInUserSession && user.signInUserSession.idToken && user.signInUserSession.idToken.payload && user.signInUserSession.idToken.payload['cognito:groups']) || [];
        setUserGroups(groups);
        setIsDesk(groups.includes('Desks'));
      } catch (error) {
        console.error('ユーザーグループの取得に失敗:', error);
        setUserGroups([]);
        setIsDesk(false);
      }
    } else {
      setUserGroups([]);
      setIsDesk(false);
    }
  }, [user]);

  // 子に渡す user を合成：親から来た user を優先し、なければ useAuthenticator＋authSession 情報を併用
  const composedUser = React.useMemo(() => {
    const base = parentUser ?? user ?? null;
    if (!base) return null;
    // 既に signInUserSession があればそのまま使う
    if (base.signInUserSession) return base;
    // authSession に idToken があれば簡易セッションを埋める
    if (authSession && (authSession.idToken || (authSession.user && authSession.user.signInUserSession))) {
      const sessionFromAuth = authSession.user && authSession.user.signInUserSession ? authSession.user.signInUserSession
        : { idToken: { jwtToken: authSession.idToken }, accessToken: { jwtToken: authSession.accessToken } };
      return { ...base, signInUserSession: sessionFromAuth };
    }
    return base;
  }, [parentUser, user, authSession]);

  return (
    <Router>
      <Box
        className="App"
        sx={{
          maxWidth: 1920,
          width: '100%',
          mx: 'auto',
          minHeight: '100vh',
        }}
      >
        <Routes>
          {/* ルート画面 */}
          <Route
            path="/"
            element={
              <>
                <AppHeader
                  user={composedUser}
                  isDesk={isDesk}
                  onSignOut={signOut}
                  isMobile={isMobile}
                />
                <Box
                  sx={{
                    maxWidth: 1920,
                    width: '100%',
                    mx: 'auto',
                  }}
                >
                  <FullCalendarComponent user={composedUser} isDesk={isDesk} onSignOut={signOut} />
                </Box>
              </>
            }
          />

          {/* メモ画面 */}
          <Route
            path="/memo"
            element={
              <MemoPage user={composedUser} isDesk={isDesk} onSignOut={signOut} />
            }
          />

          {/* S3ファイル一覧画面 */}
          <Route
            path="/files"
            element={
              <S3FilesPage user={composedUser} isDesk={isDesk} onSignOut={signOut} />
            }
          />
        </Routes>
      </Box>
    </Router>
  );
}

export default App;
